import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { map, values, forEach } from 'lodash'

import { ContentLoader } from 'components/_new/ui'

/**
 * Decorator for saving and propagating any sync/async data into a component
 * @param {Object.<string, function(state, props)>} storeProps
 * @returns {function(*): DecoratedComponent}
 */
const decorator = (storeProps) => (ComposedComponent) => {

  class DecoratedComponent extends Component {

    static displayName = `StoreData(${ComposedComponent.displayName})`

    static WrappedComponent = ComposedComponent

    static contextTypes = {
      store: PropTypes.object,
    }

    state = {
      data: null,
    }

    UNSAFE_componentWillMount() {
      this.resolveData()
      this.isComponentMounted = true
    }

    componentWillUnmount() {
      this.abortRequests()
      this.isComponentMounted = false
    }

    setData = (results) => {
      const data = {}
      const propNames = Object.keys(storeProps)

      forEach(propNames, (propName, index) => {
        data[propName] = results[index]
      })

      this.setState({ data })
    }

    resolveData() {
      const { store } = this.context

      // TODO try to get state from getState(), but check if it's undefined - https://sentry.scentbird.com/scentbird/scentbird-gweb-front/issues/360343/?referrer=slack
      const state = store.getState() || {}

      const requests = map(values(storeProps), (action) => {
        if (typeof action === 'function') {
          return action(state, this.props)
        }

        return action.split('.').reduce((a, b) => a && a[b], state)
      })

      const hasAsync  = requests.some((request) => (
        typeof request === 'function'
        || (request && typeof request === 'object' && typeof request.then === 'function')
      ))

      this.requests = requests

      if (hasAsync) {
        Promise.all(requests)
          .then((results) => {
            if (this.isComponentMounted) {
              this.setData(results)
            }
          })
      }
      else {
        this.setData(requests)
      }
    }

    abortRequests() {
      const { data } = this.state

      if (!data) {
        // TODO: Make abortable action promise
        forEach(this.requests, (request) => typeof request.abort === 'function' && request.abort())
      }
    }

    render() {
      const { data } = this.state

      if (!data) {
        return (
          <ContentLoader />
        )
      }

      return (
        <ComposedComponent
          {...this.props}
          {...data}
        />
      )
    }
  }

  return DecoratedComponent
}

export default decorator
